// Schema → SQL (SQLite). `snapshot()` records what a migration produced, and `diff()`
// turns the change between two snapshots into the next migration.
import { columnsOf, type Schema, type TableInfo } from "./schema.ts";

export const q = (name: string) => `"${name.replace(/"/g, '""')}"`;

/** Tables every Estra database has, by version (a snapshot records how many it has applied). */
export const SYSTEM_MIGRATIONS: string[][] = [
  [
    `CREATE TABLE IF NOT EXISTS "_changes" ("seq" INTEGER PRIMARY KEY AUTOINCREMENT, "tbl" TEXT NOT NULL, "key" TEXT NOT NULL, "op" TEXT NOT NULL, "v" INTEGER, "at" INTEGER NOT NULL, "origin" TEXT)`,
    `CREATE INDEX IF NOT EXISTS "_changes(at)" ON "_changes" ("at")`,
    `CREATE TABLE IF NOT EXISTS "_ops" ("op_key" TEXT PRIMARY KEY, "at" INTEGER NOT NULL)`,
    // A batch inserts 0 here to abort itself (failed precondition): CHECK fails → whole batch rolls back.
    `CREATE TABLE IF NOT EXISTS "_assert" ("ok" INTEGER NOT NULL CHECK ("ok" = 1))`,
  ],
  [
    // Trigger outbox: one row per write to a table with triggers, written in the commit's own batch.
    `CREATE TABLE IF NOT EXISTS "_events" ("id" INTEGER PRIMARY KEY AUTOINCREMENT, "seq" INTEGER, "tbl" TEXT NOT NULL, "path" TEXT NOT NULL, "op" TEXT NOT NULL, "before" TEXT, "after" TEXT, "origin" TEXT, "at" INTEGER NOT NULL, "attempts" INTEGER NOT NULL DEFAULT 0, "claimed_at" INTEGER, "state" INTEGER NOT NULL DEFAULT 0, "error" TEXT)`,
    `CREATE INDEX IF NOT EXISTS "_events(state,id)" ON "_events" ("state", "id")`,
  ],
];
/** Every system statement (fresh databases). */
export const SYSTEM_SQL = SYSTEM_MIGRATIONS.flat();

export interface TableSnap {
  parents: string[];
  columns: Record<string, string>;
  indexes: Record<string, string[]>;
  sides: Record<string, string>;
}
export interface Snapshot {
  version: 1;
  tables: Record<string, TableSnap>;
  /** Plugin name → migrations applied. */
  plugins?: Record<string, number>;
  /** System migrations applied (absent in snapshots older than the field = 1). */
  system?: number;
}

/** The part of a plugin migrations care about (EstraPlugin in @estra/server). */
export interface PluginMigrations {
  name: string;
  migrations: string[][];
}

/** Plugin SQL a database at `prev` hasn't had yet. */
export function pluginDiff(prev: Snapshot | null, plugins: PluginMigrations[]): string[] {
  return plugins.flatMap((p) => p.migrations.slice(prev?.plugins?.[p.name] ?? 0).flat());
}

export function snapshot(schema: Schema, plugins: PluginMigrations[] = []): Snapshot {
  const tables: Record<string, TableSnap> = {};
  for (const t of Object.values(schema.tables)) {
    tables[t.table] = {
      parents: t.parents,
      columns: Object.fromEntries(columnsOf(t).map((c) => [c.name, c.type])),
      indexes: t.indexes,
      sides: t.sides,
    };
  }
  return { version: 1, tables, system: SYSTEM_MIGRATIONS.length, ...(plugins.length ? { plugins: Object.fromEntries(plugins.map((p) => [p.name, p.migrations.length])) } : {}) };
}

const colList = (cols: string[]) => cols.map((c) => (c.endsWith(" DESC") ? `${q(c.slice(0, -5))} DESC` : q(c))).join(", ");

function createTable(name: string, t: TableSnap): string[] {
  const key = [...t.parents, "id"];
  const cols = [...key.map((k) => `${q(k)} TEXT NOT NULL`), ...Object.entries(t.columns).map(([c, ty]) => `${q(c)} ${ty}`)];
  cols.push(`"_v" INTEGER NOT NULL`, `"_created" INTEGER NOT NULL`, `"_updated" INTEGER NOT NULL`);
  const out = [`CREATE TABLE IF NOT EXISTS ${q(name)} (${cols.join(", ")}, PRIMARY KEY (${colList(key)}))`];
  for (const [ix, ixCols] of Object.entries(t.indexes)) out.push(createIndex(name, t, ix, ixCols));
  for (const side of Object.values(t.sides)) out.push(...createSide(side, t));
  return out;
}

// Subcollection indexes lead with the parent ids: queries run inside one parent.
const createIndex = (table: string, t: TableSnap, ix: string, cols: string[]) => `CREATE INDEX IF NOT EXISTS ${q(ix)} ON ${q(table)} (${colList([...t.parents, ...cols])})`;

function createSide(side: string, t: TableSnap): string[] {
  const key = [...t.parents, "id"];
  return [
    `CREATE TABLE IF NOT EXISTS ${q(side)} (${key.map((k) => `${q(k)} TEXT NOT NULL`).join(", ")}, "value" NOT NULL, PRIMARY KEY (${colList([...key, "value"])}))`,
    `CREATE INDEX IF NOT EXISTS ${q(`${side}(value)`)} ON ${q(side)} (${colList(["value", ...key])})`,
  ];
}

const backfillSide = (table: string, field: string, side: string, t: TableSnap) => {
  const key = [...t.parents, "id"];
  return `INSERT OR IGNORE INTO ${q(side)} (${colList([...key, "value"])}) SELECT ${key.map((k) => `d.${q(k)}`).join(", ")}, j.value FROM ${q(table)} AS d, json_each(d.${q(field)}) AS j WHERE d.${q(field)} IS NOT NULL AND j.type NOT IN ('object', 'array', 'null')`;
};

/** Full SQL for a fresh database. */
export function schemaToSql(schema: Schema): string[] {
  return diff(null, snapshot(schema)).statements;
}

export interface Diff {
  statements: string[];
  /** Drops and type changes, left out unless `allowDestructive`. */
  destructive: string[];
}

export function diff(prev: Snapshot | null, next: Snapshot, opts: { allowDestructive?: boolean } = {}): Diff {
  const statements: string[] = SYSTEM_MIGRATIONS.slice(prev ? (prev.system ?? 1) : 0).flat();
  const destructive: string[] = [];
  const drop = (desc: string, sql: string) => {
    destructive.push(desc);
    if (opts.allowDestructive) statements.push(sql);
  };
  const before = prev?.tables ?? {};
  for (const [name, t] of Object.entries(next.tables)) {
    const p = before[name];
    if (!p) {
      statements.push(...createTable(name, t));
      continue;
    }
    if (p.parents.join() !== t.parents.join()) throw new Error(`${name}: parent keys changed; needs a hand-written migration`);
    for (const [c, ty] of Object.entries(t.columns)) {
      if (!(c in p.columns)) statements.push(`ALTER TABLE ${q(name)} ADD COLUMN ${q(c)} ${ty}`);
      else if (p.columns[c] !== ty) destructive.push(`${name}.${c}: type ${p.columns[c]} → ${ty} (hand-written migration)`);
    }
    for (const c of Object.keys(p.columns)) if (!(c in t.columns)) drop(`${name}.${c}: column removed`, `ALTER TABLE ${q(name)} DROP COLUMN ${q(c)}`);
    for (const [ix, cols] of Object.entries(t.indexes)) if (!(ix in p.indexes)) statements.push(createIndex(name, t, ix, cols));
    for (const ix of Object.keys(p.indexes)) if (!(ix in t.indexes)) statements.push(`DROP INDEX IF EXISTS ${q(ix)}`);
    for (const [field, side] of Object.entries(t.sides)) {
      if (!(field in p.sides)) statements.push(...createSide(side, t), backfillSide(name, field, side, t));
    }
    for (const [field, side] of Object.entries(p.sides)) if (!(field in t.sides)) statements.push(`DROP TABLE IF EXISTS ${q(side)}`);
  }
  for (const [name, p] of Object.entries(before)) {
    if (name in next.tables) continue;
    drop(`${name}: table removed`, `DROP TABLE IF EXISTS ${q(name)}`);
    for (const side of Object.values(p.sides)) drop(`${side}: side table removed`, `DROP TABLE IF EXISTS ${q(side)}`);
  }
  return { statements, destructive };
}

export type { TableInfo };
